/**
 * Talking to the platform.
 *
 * One function, because there is one API and it answers in one shape. The
 * error shape is the server's: `{ error, message, ...details }`, and the
 * message was written to be shown to a person, so it is.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when signing in again is the fix, so the shell can say exactly that. */
  get needsSignIn(): boolean {
    return this.status === 401;
  }
}

export interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /**
   * Call `/api/<path>` instead of `/api/v1/<path>`.
   *
   * Signing in and redeeming a setup link live outside the versioned prefix,
   * because everything under it has already been authenticated by the time it
   * is reached. Those are the only two, and they say so here.
   */
  unversioned?: true;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', query, body, signal, unversioned } = options;

  const url = new URL(
    `${unversioned === true ? '/api' : '/api/v1'}${path}`,
    window.location.origin
  );
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined) url.searchParams.set(k, String(v));
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {})
    });
  } catch (err) {
    // A dropped connection is the single most likely failure on a phone, and
    // "Failed to fetch" tells nobody anything.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'offline', 'No connection. This will work again when you are back on.', {});
  }

  const text = await response.text();
  const payload: unknown = text === '' ? {} : safeParse(text);

  if (!response.ok) {
    const { error, message, ...details } = (payload ?? {}) as {
      error?: string;
      message?: string;
    };
    throw new ApiError(
      response.status,
      error ?? 'error',
      message ?? `The server answered ${response.status}.`,
      details
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // An HTML error page from a proxy, most likely. Showing its markup would
    // be worse than saying nothing useful came back.
    return { error: 'unreadable', message: 'The server sent something unreadable.' };
  }
}

/* ---- the shapes the screens read ---- */

export type Stage =
  | 'briefed'
  | 'shot'
  | 'editing'
  | 'internal_review'
  | 'with_client'
  | 'scheduled'
  | 'posted';

export interface Attention {
  piece_id: string;
  title: string;
  kind: string;
  stage: Stage;
  client_id: string;
  client_name: string;
  owner_id: string | null;
  owner_name: string | null;
  due: string | null;
  days_late: number | null;
  days_waiting: number | null;
}

/**
 * Something a client asked for that nobody has dealt with yet.
 *
 * Unlike the other four lists this one is not derived from where a piece sits:
 * a client can ask for a change to a reel that went out yesterday, and that
 * request is still work.
 */
export interface Asked {
  feedback_id: string;
  piece_id: string;
  piece_title: string;
  client_id: string;
  client_name: string;
  body: string;
  via: FeedbackVia;
  said_by: string | null;
  days_waiting: number;
}

export type FeedbackKind = 'changes' | 'approved' | 'note';
export type FeedbackVia = 'link' | 'whatsapp' | 'call' | 'studio';

/** What was said about a piece, with who said it and what it was about. */
export interface Feedback {
  id: string;
  piece_id: string;
  kind: FeedbackKind;
  body: string;
  via: FeedbackVia;
  contact_id: string | null;
  recorded_by: string | null;
  said_at: string;
  created_at: string;
  settled_at: string | null;
  contact_name: string | null;
  recorded_by_name: string | null;
  piece_title: string;
  client_id: string;
  client_name: string;
}

export interface TodayView {
  as_of: string;
  overdue: Attention[];
  awaiting_client: Attention[];
  due_soon: Attention[];
  ready_to_send: Attention[];
  asked_for: Asked[];
}

export interface Client {
  id: string;
  name: string;
  status: string;
  /** How the work is paid for: a monthly arrangement or a single job. */
  engagement: 'recurring' | 'one_off';
  category: string | null;
  note: string | null;
  /** Absent unless the signed-in person is a partner. */
  retainer_amount?: number | null;
  retainer_currency?: string;
  avatar_color: string;
  initials: string | null;
  archived_at: string | null;
}

export interface Progress {
  commitment_id: string;
  metric: string;
  unit: string;
  target_count: number;
  delivered: number;
  in_flight: number;
  sort_order: number;
}

export interface Piece {
  id: string;
  title: string;
  kind: string;
  stage: Stage;
  owner_id: string | null;
  due: string | null;
  weight: number;
  notes: string | null;
  sent_to_client_at: string | null;
  client_responded_at: string | null;
  posted_at: string | null;
  commitment_id: string | null;
}

export interface Person {
  id: string;
  name: string;
  role: 'partner' | 'manager' | 'team' | 'viewer';
  capacity: number | null;
  status: 'active' | 'inactive';
  /** What they do here, in their own words. */
  title: string | null;
  /** When their photo last changed; null for none. */
  avatar_at: string | null;
}

/** How somebody is on a client: the point of contact, doing the work, or only looking. */
export type AssignmentRole = 'lead' | 'member' | 'view';

export interface Assignment {
  client_id: string;
  person_id: string;
  role: AssignmentRole;
}

/** One line of a client's standing monthly plan. */
export interface PlanLine {
  id: string;
  client_id: string;
  metric: string;
  /** pieces, active (an ongoing service), project (a job done once), or any noun. */
  unit: string;
  target_count: number;
  icon: string | null;
  sort_order: number;
}

export interface Workload {
  person_id: string;
  name: string;
  capacity: number | null;
  committed: number;
  open_pieces: number;
  clients: number;
  over: boolean;
  /**
   * On more client accounts than one person can keep up with.
   *
   * A separate signal from `over`: points measure this month's work and move
   * weekly, this counts relationships, which cost attention whether or not
   * there is anything in the sheet for them. The line is on the server, in
   * `src/db/people.ts`.
   */
  too_many_clients: boolean;
}

export interface Health {
  behind: boolean;
  delivered: number;
  committed: number;
  overdue_count: number;
  longest_client_wait: number | null;
}

export interface Dashboard {
  client: Client;
  month: string;
  cycle_id: string | null;
  progress: Progress[];
  pieces: Piece[];
  team: Person[];
  links: Record<string, { system: string; externalId: string | null; url: string | null }>;
  contacts: Array<{ id: string; name: string; role: string }>;
  health: Health;
}

/**
 * A link a client can open. The token is present only on the response that
 * created it — every later read of this type has `token` undefined, because
 * the server cannot read it back either.
 */
export interface ShareLink {
  id: string;
  contact_id: string;
  cycle_id: string;
  can_approve: boolean;
  expires_at: string;
  revoked_at: string | null;
  last_seen_at: string | null;
  view_count: number;
  created_at: string;
  created_by: string | null;
}

export interface IssuedShareLink extends ShareLink {
  token: string;
  url: string;
}

/**
 * Who the server thinks is asking.
 *
 * `person` is null for a service token, which is the honest answer rather
 * than an invented record: a token is not somebody, and the screens that
 * greet a person by name should show nothing rather than a fiction.
 */
export interface Me {
  id: string;
  role: 'partner' | 'manager' | 'team' | 'viewer';
  service: boolean;
  person: Person | null;
  /** Operations and `export.*` switches an Admin has turned off for this role. */
  denied?: string[];
  /** Their own settings: notifications, landing page, date format, auto-assign. */
  preferences?: Preferences;
}

export interface Preferences {
  notifications?: Partial<Record<'email' | 'push' | 'weeklyDigest' | 'mentions' | 'overdue' | 'feedback' | 'waiting', boolean>>;
  landingPage?: 'today' | 'board' | 'tasks' | 'content' | 'calendar';
  dateFormat?: 'D MMM YYYY' | 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  autoAssign?: boolean;
}

/**
 * The board: everything in flight, cut by stage rather than by client.
 *
 * The one view that answers "what is the studio doing" instead of "what does
 * this client have". `days_in_stage` is the number that matters on it — a
 * piece is rarely in the wrong stage, it is in the right one for too long.
 */
export interface Card {
  piece_id: string;
  title: string;
  kind: string;
  stage: Stage;
  client_id: string;
  client_name: string;
  avatar_color: string;
  owner_id: string | null;
  owner_name: string | null;
  due: string | null;
  weight: number;
  days_in_stage: number;
  days_late: number | null;
  /** What to pick up first, as against `weight`, which is what it costs. */
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  /** Which commitment this counts against, or null for work nobody promised. */
  commitment_id: string | null;
}

export interface BoardView {
  as_of: string;
  month: string;
  columns: Array<{ stage: Stage; cards: Card[] }>;
  owners: Array<{ id: string; name: string }>;
}

/**
 * What the studio knows about a client.
 *
 * Pinned notes are things that stay true — brand rules, who decides, when they
 * answer. The rest is a dated log. All of it is internal; nothing here goes
 * anywhere near the client's own page.
 */
export interface Note {
  id: string;
  client_id: string;
  body: string;
  pinned: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
  author_name: string | null;
}

/**
 * A month of one client, in money and in work.
 *
 * `retainer_amount` is a string because it is a Postgres `numeric` and money
 * that has been through a float is money nobody should invoice against.
 *
 * The three retainer fields are optional because for anybody who is not a
 * partner they are not in the response at all — removed by the server rather
 * than sent as null. The whole studio needs to see who is behind; only the
 * partners need to see what each client pays.
 */
export interface MonthClient {
  client_id: string;
  client_name: string;
  cycle_id: string | null;
  closed_at: string | null;
  retainer_amount?: string | null;
  retainer_currency?: string;
  /** Whether the amount is the cycle's own record or the client's row today. */
  retainer_from?: 'cycle' | 'client' | null;
  committed: number;
  delivered: number;
  in_flight: number;
  late: number;
  with_client: number;
}

export interface MonthView {
  month: string;
  clients: MonthClient[];
  /** Absent, not empty, when the person asking is not a partner. */
  money?: Array<{ currency: string; retained: string; clients: number }>;
  unpriced?: number;
}
